/**
 * Route configuration for the application.
 * This file is the single source of truth for all routes.
 * Both Vue Router and the sitemap generator use this config.
 */

export interface RouteMeta extends Record<string | number | symbol, unknown> {
  /** Page title (appears in browser tab and search results) */
  title: string;
  /** Meta description for SEO */
  description: string;
  /** Optional keywords for SEO */
  keywords?: string;
  /** Optional Open Graph image URL */
  ogImage?: string;
}

// Augment vue-router's RouteMeta so route.meta is properly typed
declare module 'vue-router' {
  interface RouteMeta {
    title: string;
    description: string;
    keywords?: string;
    ogImage?: string;
  }
}

export interface RouteConfig {
  path: string;
  name: string;
  /** Include in sitemap (default: true) */
  sitemap?: boolean;
  /** View component name (without 'View.vue' suffix) */
  view: string;
  /** Whether this route uses the full layout with header/footer (default: true) */
  fullLayout?: boolean;
  /** SEO meta information */
  meta: RouteMeta;
}

const SITE_NAME = 'Jasmine Puozzo Wellness Centre';

/**
 * All application routes.
 * Add new routes here and they will automatically be:
 * - Added to Vue Router
 * - Added to the sitemap (unless sitemap: false)
 * - Pre-rendered with proper meta tags (SSG)
 */
export const routeConfigs: RouteConfig[] = [
  {
    path: '',
    name: 'Home',
    view: 'Home',
    meta: {
      title: `${SITE_NAME}`,
      description: 'Experience ultimate relaxation at Jasmine Puozzo Wellness Centre. Professional massage therapy, beauty treatments, and wellness services in a serene environment.',
      keywords: 'massage naas,lymphatic drainage massage naas,jasmine massage,massage in naas,sports massage naas,massage kildare,thai massage naas,pregnancy massage,prenatal massage,lymphatic drainage,reflexology,head and neck massage,pain relief massage',
      ogImage: '/img/opengraph/home.webp',
    },
  },
  {
    path: 'linktree',
    name: 'Linktree',
    view: 'Linktree',
    fullLayout: false,
    sitemap: false,
    meta: {
      title: `Links | ${SITE_NAME}`,
      description: 'Quick links to book appointments, find us, and connect on social media.',
      ogImage: '/img/opengraph/linktree.webp',
    },
  },
  {
    path: 'services/relaxing-massage',
    name: 'Relaxing Massage',
    view: 'services/RelaxingMassage',
    meta: {
      title: `Relaxing Massage | ${SITE_NAME}`,
      description: 'Unwind with our relaxing massage therapy. Release tension and restore balance with gentle, soothing techniques.',
      keywords: 'relaxing massage, Swedish massage, stress relief, relaxation therapy',
      ogImage: '/img/opengraph/relaxing-massage.webp',
    },
  },
  {
    path: 'services/lymphatic-drainage',
    name: 'Lymphatic Drainage',
    view: 'services/LymphaticDrainage',
    meta: {
      title: `Lymphatic Drainage Massage | ${SITE_NAME}`,
      description: 'Professional manual lymphatic drainage massage to reduce swelling, boost immunity, and support detoxification.',
      keywords: 'lymphatic drainage, MLD, detox massage, reduce swelling, immune support',
      ogImage: '/img/opengraph/lymphatic-drainage.webp',
    },
  },
  {
    path: 'services/head-and-neck-massage',
    name: 'Head and Neck Massage',
    view: 'services/HeadAndNeckMassage',
    meta: {
      title: `Head and Neck Massage | ${SITE_NAME}`,
      description: 'Relieve tension headaches and neck stiffness with our specialized head and neck massage therapy.',
      keywords: 'head massage, neck massage, tension headache relief, scalp massage',
      ogImage: '/img/opengraph/head-and-neck-massage.webp',
    },
  },
  {
    path: 'services/pain-relief-massage',
    name: 'Pain Relief Massage',
    view: 'services/PainReliefMassage',
    meta: {
      title: `Pain Relief Massage | ${SITE_NAME}`,
      description: 'Targeted deep tissue massage therapy for chronic pain relief and muscle recovery.',
      keywords: 'pain relief massage, deep tissue, chronic pain, muscle recovery',
      ogImage: '/img/opengraph/pain-relief-massage.webp',
    },
  },
  {
    path: 'services/pregnancy-massage',
    name: 'Pregnancy Massage',
    view: 'services/PregnancyMassage',
    meta: {
      title: `Pregnancy Massage | ${SITE_NAME}`,
      description: 'Safe and gentle prenatal massage designed for expecting mothers. Ease pregnancy discomforts naturally.',
      keywords: 'pregnancy massage, prenatal massage, maternity massage, expecting mothers',
      ogImage: '/img/opengraph/pregnancy-massage.webp',
    },
  },
  {
    path: 'services/sports-massage',
    name: 'Sports Massage',
    view: 'services/SportsMassage',
    meta: {
      title: `Sports Massage | ${SITE_NAME}`,
      description: 'Professional sports massage for athletes and active individuals. Improve performance and speed up recovery.',
      keywords: 'sports massage, athletic massage, muscle recovery, performance',
      ogImage: '/img/opengraph/sports-massage.webp',
    },
  },
  {
    path: 'services/reflexology',
    name: 'Reflexology',
    view: 'services/Reflexology',
    meta: {
      title: `Reflexology | ${SITE_NAME}`,
      description: 'Professional reflexology therapy to restore balance, reduce stress, and promote natural healing through targeted pressure points on the feet.',
      keywords: 'reflexology, foot massage, pressure points, holistic therapy, stress relief',
      ogImage: '/img/opengraph/reflexology.webp',
    },
  },
  {
    path: 'services/beauty-skincare',
    name: 'Beauty & Skincare',
    view: 'services/BeautySkincare',
    meta: {
      title: `Beauty & Skincare | ${SITE_NAME}`,
      description: 'Professional skincare treatments including microneedling, probiotic detox facials, dermabrasion peels, and more. Reveal your natural glow.',
      keywords: 'beauty treatments, skincare, microneedling, facial, dermabrasion, probiotic detox, vitamin c treatment, naas',
      ogImage: '/img/opengraph/beauty--skincare.webp',
    },
  },
  {
    path: 'gift-vouchers',
    name: 'Gift Vouchers',
    view: 'GiftVouchers',
    meta: {
      title: `Gift Vouchers | ${SITE_NAME}`,
      description: 'Give the gift of wellness. Purchase a Jasmine Puozzo gift voucher — perfect for birthdays, anniversaries, or just to show someone you care.',
      keywords: 'gift voucher, wellness gift, massage voucher, spa gift card, birthday gift naas, gift ideas kildare',
      ogImage: '/img/opengraph/gift-vouchers.webp',
    },
  },
  {
    path: 'join-our-team',
    name: 'Join Our Team',
    view: 'JoinOurTeam',
    meta: {
      title: `Join Our Team | ${SITE_NAME}`,
      description: 'Interested in working with Jasmine Puozzo Wellness Centre? Submit your details and CV to express your interest in joining our team.',
      keywords: 'join our team, massage therapist jobs naas, beauty therapist jobs, wellness careers, spa jobs kildare',
    },
  },
  {
    path: 'privacy',
    name: 'Privacy',
    view: 'Privacy',
    sitemap: false,
    meta: {
      title: `Privacy Policy | ${SITE_NAME}`,
      description: 'Privacy policy and data protection information for Jasmine Puozzo Wellness Centre.',
      ogImage: '/img/opengraph/privacy.webp',
    },
  },
  {
    path: '404',
    name: 'NotFound',
    view: 'NotFound',
    sitemap: false,
    meta: {
      title: `Page Not Found | ${SITE_NAME}`,
      description: 'The page you are looking for does not exist.',
    },
  },
];

/**
 * Get all route paths that should be included in the sitemap.
 */
export function getSitemapPaths(): string[] {
  return routeConfigs
    .filter((route) => route.sitemap !== false)
    .map((route) => (route.path === '' ? '/' : `/${route.path}`));
}
